/**
 * 价格相关工具函数 (Vue2版本)
 * 处理价格区间的格式化和显示
 */

/**
 * 获取价格名称列表
 * @param {Object} priceRange 价格区间对象 {min: string, max: string}
 * @param {string} name 价格字段名称
 * @returns {Array} 价格名称列表
 */
export function getPriceNameList(priceRange, name) {
  let nameList = []
  if (priceRange.min === '') {
    if (priceRange.max === '') {
      nameList = []
    }
    else {
      nameList = [`${name ? `${name}:` : ''}<=${priceRange.max}`]
    }
  }
  else {
    if (priceRange.max === '') {
      nameList = [`${name ? `${name}:` : ''}>=${priceRange.min}`]
    }
    else {
      nameList = [`${name ? `${name}:` : ''}${priceRange.min}-${priceRange.max}`]
    }
  }
  return nameList
}

/**
 * 价格相关的hooks
 * @returns {Object} 价格相关的方法
 */
export function usePrice() {
  /**
   * 验证价格输入
   * @param {string} value 价格值
   * @returns {boolean} 是否有效
   */
  function validatePrice(value) {
    if (!value) return true
    const num = parseFloat(value)
    return !isNaN(num) && num >= 0
  }

  /**
   * 格式化价格显示
   * @param {string|number} price 价格
   * @returns {string} 格式化后的价格
   */
  function formatPrice(price) {
    if (!price) return ''
    const num = parseFloat(price)
    if (isNaN(num)) return ''
    return num.toFixed(2)
  }

  /**
   * 验证价格区间
   * @param {Object} priceRange 价格区间 {min: string, max: string}
   * @returns {Object} 验证结果 {valid: boolean, message: string}
   */
  function validatePriceRange(priceRange) {
    const { min, max } = priceRange

    // 检查格式
    if (min && !validatePrice(min)) {
      return { valid: false, message: '最低价格格式不正确' }
    }

    if (max && !validatePrice(max)) {
      return { valid: false, message: '最高价格格式不正确' }
    }

    // 检查范围
    if (min && max) {
      const minNum = parseFloat(min)
      const maxNum = parseFloat(max)

      if (minNum > maxNum) {
        return { valid: false, message: '最低价格不能大于最高价格' }
      }
    }

    return { valid: true, message: '' }
  }

  /**
   * 清空价格区间
   * @returns {Object} 空的价格区间对象
   */
  function clearPriceRange() {
    return { min: '', max: '' }
  }

  /**
   * 检查价格区间是否为空
   * @param {Object} priceRange 价格区间
   * @returns {boolean} 是否为空
   */
  function isPriceRangeEmpty(priceRange) {
    return !priceRange.min && !priceRange.max
  }

  /**
   * 获取价格区间的显示文本
   * @param {Object} priceRange 价格区间
   * @param {string} unit 价格单位，默认为空
   * @returns {string} 显示文本
   */
  function getPriceRangeText(priceRange, unit = '') {
    const { min, max } = priceRange

    if (!min && !max) {
      return ''
    }

    if (min && max) {
      return `${min}${unit} - ${max}${unit}`
    }

    if (min) {
      return `≥ ${min}${unit}`
    }

    if (max) {
      return `≤ ${max}${unit}`
    }

    return ''
  }

  return {
    validatePrice,
    formatPrice,
    validatePriceRange,
    clearPriceRange,
    isPriceRangeEmpty,
    getPriceRangeText
  }
}
